import { useCallback, useEffect, useMemo, useState } from "react";
import { api, errorToMessage } from "@/lib/apiClient";
import Modal, { ConfirmDialog } from "@/components/Modal";
import {
  EmptyState, ErrorMessage, PageHeader, Pagination, SearchInput, StatusBadge, StatusFilter, Toolbar,
} from "@/components/DataTableParts";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";

const ENDPOINT = "/warranty-pricing-rules";

const emptyForm = {
  code: "",
  applies_from_offset: -365,
  applies_to_offset: -16,
  percent_of_order: "",
  flat_amount: "",
  min_price: "",
  max_price: "",
  priority: 100,
  is_active: true,
  valid_from: "",
  valid_to: "",
};

const clean = (obj) => {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === "" || v === null || v === undefined) continue;
    out[k] = v;
  }
  return out;
};

export default function PricingRulesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [isActive, setIsActive] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const [qDebounced, setQDebounced] = useState(q);
  useEffect(() => { const t = setTimeout(() => setQDebounced(q), 300); return () => clearTimeout(t); }, [q]);

  const params = useMemo(() => {
    const p = { page, page_size: 10 };
    if (qDebounced) p.q = qDebounced;
    if (isActive !== null) p.is_active = isActive;
    return p;
  }, [page, qDebounced, isActive]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(ENDPOINT, { params });
      setData(r.data);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("PricingRulesPage.load failed", err?.message || err);
      setData({ items: [], meta: null });
    } finally { setLoading(false); }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setFormError(""); setShowForm(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      code: row.code,
      applies_from_offset: row.applies_from_offset,
      applies_to_offset: row.applies_to_offset,
      percent_of_order: row.percent_of_order ?? "",
      flat_amount: row.flat_amount ?? "",
      min_price: row.min_price ?? "",
      max_price: row.max_price ?? "",
      priority: row.priority,
      is_active: row.is_active,
      valid_from: row.valid_from ?? "",
      valid_to: row.valid_to ?? "",
    });
    setFormError(""); setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    setSubmitting(true);
    try {
      const payload = clean({
        ...form,
        applies_from_offset: Number(form.applies_from_offset),
        applies_to_offset: Number(form.applies_to_offset),
        percent_of_order: form.percent_of_order === "" ? null : String(form.percent_of_order),
        flat_amount: form.flat_amount === "" ? null : String(form.flat_amount),
        min_price: form.min_price === "" ? null : String(form.min_price),
        max_price: form.max_price === "" ? null : String(form.max_price),
        priority: Number(form.priority),
      });
      if (editing) {
        const { code, ...upd } = payload;
        await api.patch(`${ENDPOINT}/${editing.id}`, upd);
      } else {
        await api.post(ENDPOINT, payload);
      }
      setShowForm(false);
      await load();
    } catch (err) { setFormError(errorToMessage(err)); }
    finally { setSubmitting(false); }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    await api.delete(`${ENDPOINT}/${toDelete.id}`);
    await load();
  };

  return (
    <div>
      <PageHeader
        title="Warranty Pricing Rules"
        subtitle="Compute renewal price automatically based on days-from-expiry window."
        actions={
          <button onClick={openCreate} data-testid="btn-new-rule" className="inline-flex items-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-sm">
            <Plus size={16} /> New Rule
          </button>
        }
      />
      <Toolbar>
        <SearchInput value={q} onChange={setQ} placeholder="Search by code…" />
        <StatusFilter value={isActive} onChange={setIsActive} />
      </Toolbar>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="rules-table">
          <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Code</th>
              <th className="text-right px-4 py-2 font-medium">From</th>
              <th className="text-right px-4 py-2 font-medium">To</th>
              <th className="text-right px-4 py-2 font-medium">Percent</th>
              <th className="text-right px-4 py-2 font-medium">Flat ₹</th>
              <th className="text-right px-4 py-2 font-medium">Min / Max ₹</th>
              <th className="text-right px-4 py-2 font-medium">Priority</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-right px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500"><Loader2 className="inline animate-spin" size={16}/> Loading…</td></tr>
            ) : data?.items?.length ? (
              data.items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono text-slate-700">{r.code}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.applies_from_offset}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.applies_to_offset}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.percent_of_order ?? "—"}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.flat_amount ?? "—"}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-600">{r.min_price ?? "—"} / {r.max_price ?? "—"}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.priority}</td>
                  <td className="px-4 py-2"><StatusBadge active={r.is_active} /></td>
                  <td className="px-4 py-2 text-right">
                    <button data-testid={`edit-${r.code}`} onClick={() => openEdit(r)} className="text-slate-600 hover:text-slate-900 mr-2"><Pencil size={16} /></button>
                    <button data-testid={`delete-${r.code}`} onClick={() => setToDelete(r)} className="text-rose-600 hover:text-rose-800"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={9}><EmptyState label="No pricing rules yet." /></td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination meta={data?.meta} page={page} onPage={setPage} />

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? `Edit ${editing.code}` : "New Pricing Rule"} size="lg">
        <form onSubmit={submit} className="space-y-3" data-testid="rule-form">
          <ErrorMessage message={formError} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code (UPPER_SNAKE)"><input required disabled={!!editing} className="input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} pattern="^[A-Z0-9_]+$" data-testid="field-code" /></Field>
            <Field label="Priority"><input type="number" min={0} max={10000} className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="field-priority" /></Field>
            <Field label="Applies From Offset"><input type="number" required className="input" value={form.applies_from_offset} onChange={(e) => setForm({ ...form, applies_from_offset: e.target.value })} data-testid="field-from" /></Field>
            <Field label="Applies To Offset"><input type="number" required className="input" value={form.applies_to_offset} onChange={(e) => setForm({ ...form, applies_to_offset: e.target.value })} data-testid="field-to" /></Field>
            <Field label="Percent of Order (0–100)"><input type="number" step="0.01" min={0} max={100} className="input" value={form.percent_of_order} onChange={(e) => setForm({ ...form, percent_of_order: e.target.value })} data-testid="field-percent" /></Field>
            <Field label="Flat Amount (₹) — alternative to percent"><input type="number" step="0.01" min={0} className="input" value={form.flat_amount} onChange={(e) => setForm({ ...form, flat_amount: e.target.value })} data-testid="field-flat" /></Field>
            <Field label="Min Price (₹)"><input type="number" step="0.01" min={0} className="input" value={form.min_price} onChange={(e) => setForm({ ...form, min_price: e.target.value })} data-testid="field-min" /></Field>
            <Field label="Max Price (₹)"><input type="number" step="0.01" min={0} className="input" value={form.max_price} onChange={(e) => setForm({ ...form, max_price: e.target.value })} data-testid="field-max" /></Field>
            <Field label="Valid From"><input type="date" className="input" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} /></Field>
            <Field label="Valid To"><input type="date" className="input" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="field-active"/> Active
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 text-sm rounded-md border border-slate-300">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="rule-submit" className="px-3 py-1.5 text-sm rounded-md bg-slate-900 text-white disabled:opacity-60">
              {submitting ? "Saving…" : editing ? "Save Changes" : "Create Rule"}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog open={!!toDelete} onClose={() => setToDelete(null)} onConfirm={doDelete}
        title="Delete pricing rule?" message={`This will soft-delete "${toDelete?.code}".`} confirmLabel="Delete" />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
